import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from '@/lib/apiClient'
import { API_ROUTES, PAGINATION } from '@/constants/apiRoutes'
import { CreatedUserResponse, CreateUserParam, EditUserParam, UserManagementList } from '@/types/index'

// Async thunk to fetch user list with optional filters
export const getUserList = createAsyncThunk<UserManagementList, { userName?: string, email?: string, accessRole?: string, page?: number, pageSize?: number, affiliation?: string }>(`${API_ROUTES.ADMIN_USER_LIST_ENDPOINT}`, async ({ userName, email, accessRole, page, pageSize, affiliation }) => {
    // Prepare query parameters
    const rawParams = {
        user_name: userName,
        email: email,
        access_role: accessRole,
        affiliation: affiliation,
        page: page,
        page_size: pageSize,
    };

    // Remove undefined or empty string parameters
    const params = new URLSearchParams();
    Object.entries(rawParams).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
            params.append(key, String(value));
        }
    });

    // API call to fetch user list
    const res = await apiClient.get<UserManagementList>(
        `${API_ROUTES.ADMIN_USER_LIST_ENDPOINT}?${params.toString()}`
    );
    return res;
})
export const createUser = createAsyncThunk<CreatedUserResponse, CreateUserParam>(API_ROUTES.ADMIN_USER_CREATE_ENDPOINT, async (createUserParam) => {
    const res = await apiClient.post<CreatedUserResponse>(API_ROUTES.ADMIN_USER_CREATE_ENDPOINT, { ...createUserParam });
    return res;
})
export const editUser = createAsyncThunk<CreatedUserResponse, EditUserParam>(API_ROUTES.ADMIN_USER_EDIT_ENDPOINT, async (editUserParam) => {
    const { user_id, ...rest } = editUserParam;
    const res = await apiClient.put<CreatedUserResponse>(`${API_ROUTES.ADMIN_USER_EDIT_ENDPOINT}/${user_id}`, { ...rest });
    return res;
})
export const deleteUser = createAsyncThunk<CreatedUserResponse, { userId: string }>(API_ROUTES.ADMIN_USER_DELETE_ENDPOINT, async ({ userId }) => {
    const res = await apiClient.put<CreatedUserResponse>(`${API_ROUTES.ADMIN_USER_DELETE_ENDPOINT}/${userId}`, {});
    return res;
})

// Initial state
const initialState: UserManagementList = {
    items: [],
    page: PAGINATION.PAGE,
    page_size: PAGINATION.PAGE_SIZE,
    status: '',
    total: 0,
    loading: false,
    error: null,
    message: '',
};

// Slice definition
const userManagementSlice = createSlice({
    name: 'userManagement',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getUserList.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.status = 'loading';
            })
            .addCase(getUserList.fulfilled, (state, action) => {
                state.items = action.payload.items || [];
                state.page = action.payload.page || PAGINATION.PAGE;
                state.page_size = action.payload.page_size || PAGINATION.PAGE_SIZE;
                state.total = action.payload.total || 0;
                state.loading = false;
                state.status = 'success';
                state.message = action.payload.message || '';
            })
            .addCase(getUserList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || action.error.message || 'Error';
                state.status = 'error';
                state.items = [];
            })
            .addCase(createUser.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.status = 'loading';
            })
            .addCase(createUser.fulfilled, (state) => {
                state.loading = false;
                state.status = 'success';
                state.message = '';
            })
            .addCase(createUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || action.error.message || '不明なエラーが発生しました。';
                state.status = 'error';
            })
            .addCase(editUser.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.status = 'loading';
            })
            .addCase(editUser.fulfilled, (state) => {
                state.loading = false;
                state.status = 'success';
                state.message = '';
            })
            .addCase(editUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || action.error.message || '不明なエラーが発生しました。';
                state.status = 'error';
            })
            .addCase(deleteUser.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.status = 'loading';
            })
            .addCase(deleteUser.fulfilled, (state) => {
                state.loading = false;
                state.status = 'success';
                state.message = '';
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || action.error.message || '不明なエラーが発生しました。';
                state.status = 'error';
            })
    },
});

export default userManagementSlice.reducer;